import { Asset } from 'src/app/api/asset/asset';
import { AssetSearchResult } from 'src/app/api/asset/search/assetSearchResult';
import { OfferingTree } from 'src/app/api/offeringTree/offeringTree';
import { Site } from 'src/app/api/site/site';
import { getAssetDisplayName } from 'src/app/utils/asset-utils';

export class ProductTreeNode {
    private static readonly DRIVES_ROOT_CID: string = '9AAC100211';
    private static readonly MOTORS_AND_GENERATORS_ROOT_CID: string = '9AAC133417';

    assetId: string | null | undefined;
    siteId: number | null | undefined;
    name: string | null | undefined;
    children: ProductTreeNode[];
    searchCids: string[] = [];
    underlyingData: Site | OfferingTree | Asset | null;
    assetsLoaded: boolean = false;
    offeringTreeLoaded: boolean = false;
    type: string = "site";
    level: number = 0;

    constructor() {
        this.children = [];
        this.underlyingData = null;
        this.assetsLoaded = false;
        this.offeringTreeLoaded = false;
    }

    get underlyingAsset(): Asset | null {
        return this.underlyingData as Asset;
    }

    get underlyingSite(): Site | null {
        return this.underlyingData as Site;
    }

    get underlyingOfferingTree(): OfferingTree | null {
        return this.underlyingData as OfferingTree;
    }

    get assets(): ProductTreeNode[] {
        return this.children.filter(x => x.type == 'asset');
    }

    get hasAssets(): boolean {
        return this.children.filter(x => x.type == 'asset').length > 0;
    }

    get treeNodes(): ProductTreeNode[] {
        return this.children.filter(x => x.type != 'asset');
    }

    hasChildren(): boolean {
        return (this.children.filter(x => x.type != 'asset').length > 0 || !this.assetsLoaded) && this.type != "asset";
    }

    static fromAsset(asset: AssetSearchResult): ProductTreeNode {
        var data = new ProductTreeNode();
        data.siteId = null;
        data.assetId = asset.assetId;
        data.assetsLoaded = true;

        data.name = getAssetDisplayName(asset);
        data.children = [];
        data.type = "asset";

        return data;
    }

    static fromSite(site: Site): ProductTreeNode {
        var data = new ProductTreeNode();
        data.underlyingData = site;
        data.siteId = site.id;
        data.assetId = null;
        data.name = site.name;
        data.type = "site";

        const parentDrives: OfferingTree = {
            cid: ProductTreeNode.DRIVES_ROOT_CID,
            Description: 'Drives',
            parentCid: null,
        };

        const parentMotorsAndGenerators: OfferingTree = {
            cid: ProductTreeNode.MOTORS_AND_GENERATORS_ROOT_CID,
            Description: 'Motors and Generators',
            parentCid: null,
        };

        data.children = [parentDrives, parentMotorsAndGenerators].map(x => ProductTreeNode.fromOfferingTree(x, 1));

        return data;
    }

    static fromOfferingTree(offeringTree: OfferingTree, level: number): ProductTreeNode {
        var data = new ProductTreeNode();
        data.underlyingData = offeringTree;
        data.siteId = null;
        data.assetId = null;
        data.name = offeringTree.Description;
        data.children = offeringTree.Children
            ? offeringTree.Children
                .sort((x, y) => (x.Description ?? '').localeCompare(y.Description ?? ''))
                .map(x => ProductTreeNode.fromOfferingTree(x, level + 1))
            : [];
        data.type = "offering-tree";
        data.level = level;
        data.searchCids = offeringTree.cid ? [offeringTree.cid] : [];

        return data;
    }

    loadDataForAssetSearchOnOfferingTree(childrenNodes: OfferingTree[]): void {
        childrenNodes.forEach(x => {
            this.searchCids = this.computeSearchCids(x);
        });
    }

    private computeSearchCids(offeringTree: OfferingTree): string[] {
        let searchCids: string[] = offeringTree.Children?.filter(x => x.cid).map(x => x.cid!) || [];

        return searchCids.concat(offeringTree.Children?.flatMap(x => this.computeSearchCids(x)) || []);
    }
}
